import * as z from 'zod';
import { MapCoordinates } from '../data-access/map-config.tokens';

/**
 * Each layer carries its own state, so a pending or failed one never blocks the map or the
 * other layers. Emptiness is `success` with a zero-length collection — there is deliberately
 * no `empty` status.
 */
export type AsyncState<T> =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'success'; readonly data: T }
  | { readonly status: 'error'; readonly error: string }
  // Waiting on ORS quota after a rescheduled request — deliberately distinct from both
  // `loading` and `error`. `nextAttemptAt` is the retry time the backend scheduled, null
  // until one is known.
  | { readonly status: 'throttled'; readonly nextAttemptAt: string | null };

export const asyncIdle = <T>(): AsyncState<T> => ({ status: 'idle' });
export const asyncLoading = <T>(): AsyncState<T> => ({ status: 'loading' });
export const asyncSuccess = <T>(data: T): AsyncState<T> => ({
  status: 'success',
  data,
});
export const asyncError = <T>(error: string): AsyncState<T> => ({
  status: 'error',
  error,
});
export const asyncThrottled = <T>(
  nextAttemptAt: string | null,
): AsyncState<T> => ({ status: 'throttled', nextAttemptAt });

/** The values the API emits; any landuse it cannot classify arrives as `unknown`. */
export const ZONING_SUITABILITIES = [
  'high',
  'medium',
  'low',
  'unknown',
] as const;
export type ZoningSuitability = (typeof ZONING_SUITABILITIES)[number];

/**
 * An OSM parking area near a site. The source ring arrives as `[[lat, lon], ...]` and is
 * normalized on the way in, so callers never handle raw source ordering.
 */
export interface ParkingArea {
  id: string;
  /** Blank when OSM supplies none. */
  name: string;
  polygon: MapCoordinates[];
}

/** A zoning (Gewerbezone) area near a site. */
export interface ZoningArea {
  id: string;
  /** Zone code, e.g. "GE", "MI", "SO". */
  zoneType: string;
  /** Human-readable label, e.g. "Gewerbegebiet". */
  zoneLabel: string;
  suitability: ZoningSuitability;
  polygon: MapCoordinates[];
}

/**
 * Ordered nearest-to-farthest: the order the isochrones are drawn (largest first) and the
 * order the legend reads. There is no `outside` band because the backend cuts the competition
 * catchment at 10 minutes.
 */
export const DRIVE_TIME_BANDS = ['2min', '5min', '10min'] as const;
export type DriveTimeBand = (typeof DRIVE_TIME_BANDS)[number];

/**
 * Upper bounds are inclusive: 2.00 min is `'2min'`, 2.01 min is `'5min'`. Isochrone polygons
 * and competitor markers both colour through this one classifier, so a marker's colour always
 * matches the polygon it sits in. Values above 10 clamp instead of throwing — upstream data is
 * cut at 10 minutes, so the clamp only keeps the return type total.
 */
export function driveTimeBand(minutes: number): DriveTimeBand {
  if (minutes <= 2) {
    return '2min';
  }
  if (minutes <= 5) {
    return '5min';
  }
  return '10min';
}

/**
 * Drive time is an ordered quantity, so the bands share one darkening hue instead of three
 * unrelated ones: monotonic lightness keeps the order legible without hue discrimination, and
 * the legend, the table's numeric column and the marker tooltip all repeat the information
 * (WCAG 1.4.1). Literals rather than imports — this library must not depend on the app, and
 * the Google Maps SDK cannot resolve a CSS variable.
 */
export const BAND_COLORS: Record<DriveTimeBand, string> = {
  '2min': '#CBD3FF', // cobalt ramp step 2
  '5min': '#6E86FF', // cobalt ramp step 4
  '10min': '#233C84', // cobalt ramp step 6
};

/**
 * The source polygon is GeoJSON `[lon, lat]` and is normalized on the way in. Bands are
 * filled catchment areas, not annular rings — 2 min nests inside 5 min inside 10 min — so
 * they must be drawn largest-first to stay readable.
 */
export interface IsochroneBand {
  /** Drive-time threshold in minutes (2 / 5 / 10). */
  timeMinutes: number;
  polygon: MapCoordinates[];
  /** Optional metadata for a tooltip; not load-bearing. */
  areaKm2?: number;
}

/**
 * One competing station per record, not the per-operator aggregate the Python prototype
 * produced. Power bands are exclusive (≤150, 150–300, >300 kW) and sum to the station's total
 * charge-point count.
 */
export interface CompetitorStation {
  /** Minted from operator + name + coords; the source supplies no id. */
  id: string;
  name: string;
  operator: string;
  chargePoints: { upTo150: number; upTo300: number; over300: number };
  /** Normalized from the source's separate lat/lng fields. */
  position: MapCoordinates;
  /** Drives the band grouping. */
  driveTimeMinutes: number;
}

// --- Backend bundle contract -------------------------------------------------

/**
 * Parsed at the API-repository boundary, so contract drift surfaces as an error instead of a
 * corrupt overlay. Coordinates arrive already normalized, so nothing is flipped here.
 */
const mapCoordinatesSchema = z.object({
  lat: z.number(),
  lng: z.number(),
}) satisfies z.ZodType<MapCoordinates>;

export const parkingAreaSchema = z.object({
  id: z.string(),
  name: z.string(),
  polygon: z.array(mapCoordinatesSchema),
}) satisfies z.ZodType<ParkingArea>;

export const zoningAreaSchema = z.object({
  id: z.string(),
  zoneType: z.string(),
  zoneLabel: z.string(),
  suitability: z.enum(ZONING_SUITABILITIES),
  polygon: z.array(mapCoordinatesSchema),
}) satisfies z.ZodType<ZoningArea>;

export const isochroneBandSchema = z.object({
  timeMinutes: z.number(),
  polygon: z.array(mapCoordinatesSchema),
  areaKm2: z.number().optional(),
}) satisfies z.ZodType<IsochroneBand>;

export const competitorStationSchema = z.object({
  id: z.string(),
  name: z.string(),
  operator: z.string(),
  chargePoints: z.object({
    upTo150: z.number(),
    upTo300: z.number(),
    over300: z.number(),
  }),
  position: mapCoordinatesSchema,
  driveTimeMinutes: z.number(),
}) satisfies z.ZodType<CompetitorStation>;

export const LAYER_STATUSES = [
  'pending',
  'processing',
  'throttled',
  'success',
  'error',
] as const;
export type LayerStatus = (typeof LAYER_STATUSES)[number];

/**
 * `nextAttemptAt` is the scheduled retry for a `throttled` layer: null on every other status,
 * and always null for the two Overpass layers, which never throttle.
 */
function layerSchema<T>(item: z.ZodType<T>) {
  return z.object({
    status: z.enum(LAYER_STATUSES),
    computedAt: z.string().nullable(),
    error: z.string().nullable(),
    // Tolerate an API build predating the throttled contract: a missing key reads as "no
    // retry scheduled" rather than failing the whole-bundle parse and blanking the page.
    nextAttemptAt: z.string().nullable().default(null),
    data: z.array(item),
  });
}

export const siteInsightsBundleSchema = z.object({
  isochrones: layerSchema(isochroneBandSchema),
  competition: layerSchema(competitorStationSchema),
  parking: layerSchema(parkingAreaSchema),
  zoning: layerSchema(zoningAreaSchema),
});

/** The parsed bundle, before statuses become `AsyncState`s. */
export type SiteInsightsBundle = z.infer<typeof siteInsightsBundleSchema>;

/** Layers as `AsyncState`s, plus each layer's `computedAt` for the freshness note. */
export interface MappedSiteInsights {
  isochrones: AsyncState<IsochroneBand[]>;
  competition: AsyncState<CompetitorStation[]>;
  parking: AsyncState<ParkingArea[]>;
  zoning: AsyncState<ZoningArea[]>;
  computedAt: {
    isochrones: string | null;
    competition: string | null;
    parking: string | null;
    zoning: string | null;
  };
}

export function layerToAsyncState<T>(layer: {
  status: LayerStatus;
  error: string | null;
  data: T[];
  nextAttemptAt: string | null;
}): AsyncState<T[]> {
  switch (layer.status) {
    case 'pending':
    case 'processing':
      return asyncLoading();
    case 'throttled':
      return asyncThrottled(layer.nextAttemptAt);
    case 'success':
      return asyncSuccess(layer.data);
    case 'error':
      return asyncError(layer.error ?? 'error');
  }
}
